import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Grid, Chip, Tooltip,
  CircularProgress, Alert, InputAdornment, TableSortLabel,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import EditableTable from '../components/common/EditableTable';
import { clientsAPI, clientConditionsAPI } from '../services/api';

const CONTINENTS = ['EUROPA', 'AFRICA', 'ASIA', 'AMERICA_NORTE', 'AMERICA_SUR', 'OCEANIA'];

const conditionColumns = [
  { field: 'description', header: 'Descripcion', width: '25%' },
  { field: 'percentage', header: '% del Total', type: 'number' },
  { field: 'milestone', header: 'Hito' },
  { field: 'delay_days', header: 'Dias retraso', type: 'number' },
  { field: 'delay_from', header: 'Desde' },
];

const conditionFields = [
  { field: 'description', label: 'Descripcion', required: true, fullWidth: true },
  { field: 'percentage', label: '% del total', type: 'number', required: true, defaultValue: '' },
  { field: 'milestone', label: 'Hito', type: 'select', required: true, defaultValue: 'pedido', options: [
    { value: 'pedido', label: 'Pedido' }, { value: 'entrega', label: 'Entrega' },
    { value: 'factura', label: 'Factura' }, { value: 'puesta_en_marcha', label: 'Puesta en marcha' },
    { value: 'recepcion_provisional', label: 'Recepcion provisional' },
  ]},
  { field: 'delay_days', label: 'Dias de retraso', type: 'number', defaultValue: 0 },
  { field: 'delay_from', label: 'Contar desde', type: 'select', defaultValue: 'milestone', options: [
    { value: 'milestone', label: 'Fecha del hito' }, { value: 'invoice_date', label: 'Fecha factura' },
  ]},
  { field: 'order', label: 'Orden', type: 'number', defaultValue: 1 },
];

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form, setForm] = useState({});
  const [formError, setFormError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedClients = [...clients].sort((a, b) => {
    const va = a[sortField] ?? '';
    const vb = b[sortField] ?? '';
    const cmp = String(va).localeCompare(String(vb), 'es', { numeric: true });
    return sortDir === 'desc' ? -cmp : cmp;
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchQ(searchInput), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Load clients from backend
  useEffect(() => {
    loadClients(searchQ);
  }, [searchQ]);

  // Load conditions when client is selected
  useEffect(() => {
    if (selectedClient) {
      loadConditions(selectedClient.id);
    } else {
      setConditions([]);
    }
  }, [selectedClient]);

  const loadClients = async (q = '') => {
    try {
      setLoading(true);
      const resp = await clientsAPI.list(q || undefined);
      setClients(resp.data);
      setError(null);
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Error al cargar los clientes.');
    } finally {
      setLoading(false);
    }
  };

  const loadConditions = async (clientId) => {
    try {
      const resp = await clientConditionsAPI.list(clientId);
      setConditions(resp.data);
    } catch (err) {
      console.error('Error loading conditions:', err);
      setConditions([]);
    }
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const nextCode = (prefix, list) => {
    const nums = list
      .map(x => x.code)
      .filter(c => c && c.startsWith(prefix))
      .map(c => parseInt(c.replace(prefix, ''), 10))
      .filter(n => !isNaN(n));
    const max = nums.length > 0 ? Math.max(...nums) : 0;
    return `${prefix}${String(max + 1).padStart(3, '0')}`;
  };

  const handleOpenNew = () => {
    setForm({ code: nextCode('CLI-', clients), name: '', cif: '', address: '', city: '', postal_code: '', country: 'Spain', continent: 'EUROPA', contact_name: '', contact_email: '', contact_phone: '', description: '' });
    setFormError('');
    setEditingClient(null);
    setOpen(true);
  };

  const handleOpenEdit = (client) => {
    setForm({ ...client });
    setFormError('');
    setEditingClient(client);
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      setFormError('');
      if (editingClient) {
        await clientsAPI.update(editingClient.id, form);
      } else {
        await clientsAPI.create(form);
      }
      await loadClients(searchQ);
      setOpen(false);
      setError(null);
    } catch (err) {
      console.error('Error saving client:', err);
      const msg = err.response?.data?.detail || 'Error al guardar el cliente.';
      setFormError(msg);
    }
  };

  const handleDelete = async (id) => {
    try {
      await clientsAPI.delete(id);
      if (selectedClient?.id === id) setSelectedClient(null);
      await loadClients(searchQ);
    } catch (err) {
      console.error('Error deleting client:', err);
      const msg = err.response?.data?.detail || 'Error al eliminar el cliente.';
      setError(msg);
    }
  };

  const condHandlers = {
    onAdd: async (data) => {
      try {
        await clientConditionsAPI.create(selectedClient.id, data);
        await loadConditions(selectedClient.id);
      } catch (err) { console.error('Error adding condition:', err); }
    },
    onUpdate: async (id, data) => {
      try {
        await clientConditionsAPI.update(id, data);
        await loadConditions(selectedClient.id);
      } catch (err) { console.error('Error updating condition:', err); }
    },
    onDelete: async (id) => {
      try {
        await clientConditionsAPI.delete(id);
        await loadConditions(selectedClient.id);
      } catch (err) { console.error('Error deleting condition:', err); }
    },
  };

  if (loading && clients.length === 0 && !searchInput) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" fontWeight="bold">Clientes</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenNew}>Nuevo Cliente</Button>
      </Box>

      {/* Condiciones del cliente seleccionado — panel superior */}
      {selectedClient && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">
              Condiciones de Cobro: <strong>{selectedClient.name}</strong>
            </Typography>
            <Button size="small" onClick={() => setSelectedClient(null)}>Cerrar</Button>
          </Box>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Se copian automaticamente a cada nuevo proyecto de este cliente (editables por proyecto).
          </Typography>
          <EditableTable title="Condiciones" columns={conditionColumns} rows={conditions}
            dialogFields={conditionFields} emptyMessage="Sin condiciones definidas" {...condHandlers} />
        </Paper>
      )}

      {/* Search bar */}
      <TextField
        fullWidth
        size="small"
        placeholder="Buscar por nombre, CIF, ciudad, pais, contacto, email, descripcion..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
          ),
        }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#c62828' }}>
                {[['code','Codigo'],['name','Nombre'],['conditions_summary','Condiciones'],['description','Descripcion'],['cif','CIF'],['city','Ciudad'],['continent','Continente'],['contact_name','Contacto'],['contact_email','Email']].map(([field, label]) => (
                  <TableCell key={field} sx={{ fontWeight: 'bold', color: '#fff' }} sortDirection={sortField === field ? sortDir : false}>
                    <TableSortLabel active={sortField === field} direction={sortField === field ? sortDir : 'asc'} onClick={() => handleSort(field)}>{label}</TableSortLabel>
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.length === 0 ? (
                <TableRow><TableCell colSpan={10} align="center">
                  {searchInput ? `Sin resultados para "${searchInput}"` : 'No hay clientes registrados'}
                </TableCell></TableRow>
              ) : sortedClients.map((c) => (
                <TableRow key={c.id} hover selected={selectedClient?.id === c.id}
                  onClick={() => setSelectedClient(c)} sx={{ cursor: 'pointer' }}>
                  <TableCell><strong>{c.code}</strong></TableCell>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>
                    {c.conditions_summary
                      ? <Chip label={c.conditions_summary} size="small" color="primary" variant="outlined" />
                      : <Chip label="—" size="small" variant="outlined" sx={{ color: 'text.disabled' }} />}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={c.description || ''}><span>{c.description}</span></Tooltip>
                  </TableCell>
                  <TableCell>{c.cif}</TableCell>
                  <TableCell>{c.city}</TableCell>
                  <TableCell><Chip label={c.continent} size="small" variant="outlined" /></TableCell>
                  <TableCell>{c.contact_name}</TableCell>
                  <TableCell>{c.contact_email}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar"><IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenEdit(c); }}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={2}><TextField fullWidth label="Codigo" name="code" value={form.code || ''} onChange={handleChange} helperText="Editable. Ej: CLI-005" /></Grid>
            <Grid item xs={12} sm={5}><TextField fullWidth label="Nombre" name="name" value={form.name || ''} onChange={handleChange} required /></Grid>
            <Grid item xs={12} sm={2}><TextField fullWidth label="CIF" name="cif" value={form.cif || ''} onChange={handleChange} /></Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth select label="Continente" name="continent" value={form.continent || 'EUROPA'} onChange={handleChange} SelectProps={{ native: true }}>
                {CONTINENTS.map(c => <option key={c} value={c}>{c}</option>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth label="Direccion" name="address" value={form.address || ''} onChange={handleChange} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Ciudad" name="city" value={form.city || ''} onChange={handleChange} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Codigo Postal" name="postal_code" value={form.postal_code || ''} onChange={handleChange} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Pais" name="country" value={form.country || ''} onChange={handleChange} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Persona de Contacto" name="contact_name" value={form.contact_name || ''} onChange={handleChange} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Email" name="contact_email" value={form.contact_email || ''} onChange={handleChange} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Telefono" name="contact_phone" value={form.contact_phone || ''} onChange={handleChange} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Descripcion / Actividad" name="description" value={form.description || ''} onChange={handleChange} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>{editingClient ? 'Actualizar' : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
